/**
 * Hook para rastreamento de métricas de performance de settings
 * 
 * Coleta dados sobre requisições, cache hits/misses e detecção de loops infinitos
 * conforme especificado na documentação do design.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const LOOP_DETECTION_THRESHOLD = 5; // Máximo de requisições por segundo para detectar loop
const METRICS_STORAGE_KEY = 'settingsPerformanceMetrics';

const useSettingsPerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    requestCount: 0,
    cacheHits: 0,
    cacheMisses: 0,
    apiRequests: 0,
    averageResponseTime: 0,
    loopDetections: 0,
    lastReset: Date.now(),
    requestHistory: []
  });

  const requestTimestampsRef = useRef([]);
  const responseTimesRef = useRef([]);

  // Carregar métricas do localStorage na inicialização
  useEffect(() => {
    const savedMetrics = localStorage.getItem(METRICS_STORAGE_KEY);
    if (savedMetrics) {
      try {
        const parsed = JSON.parse(savedMetrics);
        setMetrics(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.warn('⚠️ Erro ao carregar métricas salvas:', error);
      }
    }
  }, []);

  // Salvar métricas no localStorage
  const saveMetrics = useCallback((newMetrics) => {
    try {
      localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(newMetrics));
    } catch (error) {
      console.warn('⚠️ Erro ao salvar métricas:', error);
    }
  }, []);

  /**
   * Registra uma nova requisição de settings
   */
  const recordRequest = useCallback((type = 'unknown', companyId = null) => {
    const timestamp = Date.now();
    
    // Adicionar timestamp para detecção de loops
    requestTimestampsRef.current.push(timestamp);
    
    // Manter apenas últimos 10 segundos de requests
    const tenSecondsAgo = timestamp - 10000;
    requestTimestampsRef.current = requestTimestampsRef.current.filter(t => t > tenSecondsAgo);
    
    // Detectar possível loop infinito
    const recentRequests = requestTimestampsRef.current.filter(t => t > timestamp - 1000);
    const isLoop = recentRequests.length > LOOP_DETECTION_THRESHOLD;
    
    if (isLoop) {
      console.error('🔄 LOOP INFINITO DETECTADO:', {
        requestsInLastSecond: recentRequests.length,
        type,
        companyId,
        timestamp: new Date(timestamp).toLocaleTimeString()
      });
    }

    setMetrics(prev => {
      const newMetrics = {
        ...prev,
        requestCount: prev.requestCount + 1,
        loopDetections: isLoop ? prev.loopDetections + 1 : prev.loopDetections,
        requestHistory: [
          ...prev.requestHistory.slice(-99), // Manter últimas 100 requisições
          {
            timestamp,
            type,
            companyId,
            isLoop
          }
        ]
      };
      
      saveMetrics(newMetrics);
      return newMetrics;
    });

    // Retornar função para registrar resposta
    return (success = true, fromCache = false) => {
      const responseTime = Date.now() - timestamp;
      responseTimesRef.current.push(responseTime);
      
      // Manter apenas últimas 50 respostas para calcular média
      if (responseTimesRef.current.length > 50) {
        responseTimesRef.current = responseTimesRef.current.slice(-50);
      }
      
      const averageResponseTime = responseTimesRef.current.reduce((sum, time) => sum + time, 0) / responseTimesRef.current.length;

      setMetrics(prev => {
        const newMetrics = {
          ...prev,
          cacheHits: fromCache ? prev.cacheHits + 1 : prev.cacheHits,
          cacheMisses: !fromCache ? prev.cacheMisses + 1 : prev.cacheMisses,
          apiRequests: !fromCache ? prev.apiRequests + 1 : prev.apiRequests,
          averageResponseTime: Math.round(averageResponseTime)
        };
        
        saveMetrics(newMetrics);
        return newMetrics;
      });
    };
  }, [saveMetrics]);

  /**
   * Registra um cache hit
   */
  const recordCacheHit = useCallback((companyId = null) => {
    const recordResponse = recordRequest('cache_hit', companyId);
    recordResponse(true, true);
  }, [recordRequest]);

  /**
   * Registra um cache miss
   */
  const recordCacheMiss = useCallback((companyId = null) => {
    const recordResponse = recordRequest('cache_miss', companyId);
    recordResponse(true, false);
  }, [recordRequest]);

  /**
   * Registra uma requisição API
   */
  const recordApiRequest = useCallback((companyId = null) => {
    return recordRequest('api_request', companyId);
  }, [recordRequest]);

  /**
   * Reset das métricas
   */
  const resetMetrics = useCallback(() => {
    const initialMetrics = {
      requestCount: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiRequests: 0,
      averageResponseTime: 0,
      loopDetections: 0,
      lastReset: Date.now(),
      requestHistory: []
    };
    
    setMetrics(initialMetrics);
    saveMetrics(initialMetrics);
    requestTimestampsRef.current = [];
    responseTimesRef.current = [];
    
    console.log('📊 Métricas de performance resetadas');
  }, [saveMetrics]);

  /**
   * Obtém estatísticas calculadas
   */
  const getCalculatedStats = useCallback(() => {
    const totalRequests = metrics.cacheHits + metrics.cacheMisses;
    const hitRate = totalRequests > 0 ? ((metrics.cacheHits / totalRequests) * 100) : 0;
    
    const now = Date.now();
    const hourAgo = now - (60 * 60 * 1000);
    const recentRequests = metrics.requestHistory.filter(req => req.timestamp > hourAgo);
    const recentLoops = recentRequests.filter(req => req.isLoop);
    
    return {
      hitRate: parseFloat(hitRate.toFixed(2)),
      totalRequests,
      requestsPerHour: recentRequests.length,
      loopsPerHour: recentLoops.length,
      averageResponseTime: metrics.averageResponseTime,
      memoryUsage: JSON.stringify(metrics).length, // Estimativa básica
      cacheEfficiency: hitRate > 80 ? 'excellent' : hitRate > 60 ? 'good' : hitRate > 40 ? 'poor' : 'critical'
    };
  }, [metrics]);

  /**
   * Verifica se há indicação de loop infinito
   */
  const hasRecentLoops = useCallback(() => {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    const recentLoops = metrics.requestHistory.filter(req => 
      req.timestamp > fiveMinutesAgo && req.isLoop
    );
    
    return recentLoops.length > 0;
  }, [metrics.requestHistory]);

  /**
   * Obtém relatório detalhado de loops
   */
  const getLoopReport = useCallback(() => {
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    const loops = metrics.requestHistory.filter(req => 
      req.timestamp > thirtyMinutesAgo && req.isLoop
    );
    
    // Agrupar por empresa
    const loopsByCompany = loops.reduce((acc, loop) => {
      const key = loop.companyId || 'unknown';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(loop);
      return acc;
    }, {});
    
    return {
      totalLoops: loops.length,
      affectedCompanies: Object.keys(loopsByCompany).length,
      loopsByCompany,
      lastLoop: loops[loops.length - 1] || null
    };
  }, [metrics.requestHistory]);

  return {
    metrics,
    calculatedStats: getCalculatedStats(),
    
    // Funções de registro
    recordRequest,
    recordCacheHit,
    recordCacheMiss,
    recordApiRequest,
    
    // Análise de performance
    hasRecentLoops: hasRecentLoops(),
    loopReport: getLoopReport(),
    
    // Utilitários
    resetMetrics
  };
};

export default useSettingsPerformanceMetrics;